import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { BackButton } from '@/components';
import { EventView } from '@/features/EventView';
import {
    EventViewErrorState,
    EventViewSkeleton,
} from '@/features/EventView/components';
import { EventFeed } from '@/features/EventFeed';
import { useDocumentTitle } from '@/hooks';
import { AppRoutes } from '@/constants/routes';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getEventCacheKey } from '@/constants/cache-keys';
import { MILLISECONDS_TO_SECONDS } from '@/constants/time';
import { EngineService } from '@/services/engine';
import { useEventFeedStore } from '@/stores/event-feed.store';
import { useQueryResultsStore } from '@/stores';
import { isEventStillProcessing } from '@/utils/event-playback-range';
import { isNotFoundError } from '@/utils/http';
import { useReadinessDeadline } from '@/features/EventView/hooks/useReadinessDeadline';
import type { FeedContext } from '@/stores/event-feed.store';
import {
    EventViewErrorReason,
    type Event,
    type ScoredEvent,
} from '@/types/event';

const EVENT_POLL_INTERVAL_MS = 5 * 1000;

interface EventLocationState {
    feedContext?: FeedContext;
}

function extractEventId(item: Event | ScoredEvent): string {
    if ('event' in item) return item.event.id;
    return item.id;
}

export default function EventPage() {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const { eventId } = useParams<{ eventId: string }>();
    const location = useLocation();
    const locationFeedContext = (location.state as EventLocationState | null)
        ?.feedContext;
    const [eventTitle, setEventTitle] = useState<string | undefined>();
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
    const setFeedInteraction = useEventFeedStore(
        (state) => state.setFeedInteraction,
    );

    useDocumentTitle('event', eventTitle);

    const onEventLoaded = useCallback((title: string) => {
        setEventTitle(title);
    }, []);

    const onFeedItemSelect = useCallback((item: Event | ScoredEvent) => {
        const nextEventId = extractEventId(item);
        setSelectedEventId(nextEventId);

        const nextPath = AppRoutes.EVENT.replace(
            ':eventId',
            encodeURIComponent(nextEventId),
        );
        const nextUrl = `${nextPath}${window.location.search}${window.location.hash}`;
        const currentUrl = [
            window.location.pathname,
            window.location.search,
            window.location.hash,
        ].join('');

        if (nextUrl !== currentUrl) {
            window.history.replaceState(window.history.state, '', nextUrl);
        }
    }, []);

    const playingEventId = selectedEventId ?? eventId ?? null;

    const queryResultEvents = useQueryResultsStore((state) => state.events);
    const similarResults = useQueryResultsStore(
        (state) => state.similarResults,
    );
    const similarResult = similarResults.find(
        (result) => result.scoredEvent.event.id === playingEventId,
    );

    const playingEventType =
        queryResultEvents.find(
            (scoredEvent) => scoredEvent.event.id === playingEventId,
        )?.event.type ?? similarResult?.scoredEvent.event.type;

    const {
        data: event,
        error,
        mutate: revalidateEvent,
    } = useSWR<Event>(
        playingEventId ? getEventCacheKey(playingEventId) : null,
        () =>
            EngineService.getEvent(playingEventId as string, playingEventType),
        {
            refreshInterval: (latest) =>
                !latest ||
                isEventStillProcessing(
                    latest,
                    Date.now() / MILLISECONDS_TO_SECONDS,
                )
                    ? EVENT_POLL_INTERVAL_MS
                    : 0,
            shouldRetryOnError: (err) => !isNotFoundError(err),
        },
    );

    useEffect(() => {
        setSelectedEventId(null);
    }, [eventId]);

    useEffect(() => {
        if (!playingEventId) return;
        setFeedInteraction(playingEventId, onFeedItemSelect);
        return () => setFeedInteraction(null, null);
    }, [playingEventId, onFeedItemSelect, setFeedInteraction]);

    const [retryAttempt, setRetryAttempt] = useState(0);
    const eventMissing = isNotFoundError(error);
    const loadFailed = useReadinessDeadline(
        !event && !eventMissing,
        `${playingEventId ?? ''}:${retryAttempt}`,
    );

    const retryEvent = () => {
        setRetryAttempt((attempt) => attempt + 1);
        void revalidateEvent();
    };

    if (!eventId || !playingEventId) {
        return (
            <div className="p-8 text-white">{t('page.eventIdNotProvided')}</div>
        );
    }

    return (
        <div className="h-full relative">
            {locationFeedContext && <EventFeed context={locationFeedContext} />}
            <BackButton variant="floating" />

            <div className="h-full flex flex-col items-center">
                <div className="w-full flex-1 min-h-0">
                    {eventMissing ? (
                        <EventViewErrorState
                            reason={EventViewErrorReason.NOT_FOUND}
                            onRetry={retryEvent}
                        />
                    ) : event ? (
                        <EventView
                            event={event}
                            title={similarResult?.description}
                            onEventLoaded={onEventLoaded}
                            refetchEvent={retryEvent}
                        />
                    ) : loadFailed ? (
                        <EventViewErrorState
                            reason={EventViewErrorReason.FAILED}
                            onRetry={retryEvent}
                        />
                    ) : (
                        <EventViewSkeleton />
                    )}
                </div>
            </div>
        </div>
    );
}
